import { Box, Chip, Divider, IconButton, Stack, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { format, parseISO } from 'date-fns';
import React, { useState } from 'react';
import { Pencil02 } from 'untitledui-js-base';
import { SURFACE_BORDER_COLOR, surfaceSx } from '../../styles/tokens';
import { TransparentChip, TransparentChipProps } from '../common';
import { Role } from '../types';
import { Accident } from '../types/accident';

interface BoxHeaderChipProps extends TransparentChipProps {
  label: string;
}

export const BoxHeaderChip = ({ label, color }: BoxHeaderChipProps) => (
  <TransparentChip
    size="small"
    label={label}
    color={color}
    variant="filled"
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      width: 'fit-content',
      marginX: 'auto',
      marginTop: '12px'
    }}
  />
);

interface AccidentItemProps extends Accident {
  extra?: React.ReactNode;
  userRole?: Role;
  onDateChange?: (date: Date) => void;
}

export const AccidentItem = ({
  status,
  labels,
  contractNumber,
  car,
  user,
  insuranceCode,
  date,
  place,
  extra,
  insuranceType,
  paymentStatus,
  userRole,
  onDateChange
}: AccidentItemProps) => {
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  return (
    <Box
      sx={{
        ...surfaceSx,
        padding: '1.5rem',
        paddingTop: !status?.progress?.isCompleted ? '3.5rem' : '1.5rem',
        position: 'relative',
        height: 1,
        borderColor: !status?.progress?.isCompleted ? 'error.main' : SURFACE_BORDER_COLOR
      }}
    >
      {!status?.progress?.isCompleted && <BoxHeaderChip label="Nehoda není kompletní" color="error" />}
      <Stack mb={2}>
        <Typography variant="h6" fontWeight="bold">
          {user
            ? [[user.degree, user.name, user.surname].filter(Boolean).join(' '), user.company?.companyName]
                .filter(Boolean)
                .join(' - ')
            : ''}
        </Typography>
        <Typography variant="h6">{[car?.brand, car?.model, car?.plateNumber].filter(Boolean).join(' ')}</Typography>
      </Stack>
      <Divider />
      <Stack gap={1} mt={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight="bold">Číslo spisu</Typography>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography fontWeight="bold">{contractNumber}</Typography>
            {insuranceType && (
              <TransparentChip
                size="small"
                label={insuranceType.toUpperCase()}
                color="info"
                sx={{ fontSize: '10px', height: '18px' }}
              />
            )}
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight="bold">Číslo pojistné události</Typography>
          <Typography fontWeight="bold">{insuranceCode}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight="bold">Datum vzniku</Typography>
          <Stack direction="row" alignItems="center" position="relative">
            {date && <Typography fontWeight="bold">{format(date, 'dd.MM.yyyy HH:mm')}</Typography>}
            {onDateChange && (
              <>
                <IconButton size="small" color="primary" onClick={() => setDatePickerOpen(true)}>
                  <Pencil02 size="16px" />
                </IconButton>
                <DateTimePicker
                  open={datePickerOpen}
                  onClose={() => setDatePickerOpen(false)}
                  onAccept={(newDate) => newDate && onDateChange(newDate)}
                  value={date ? parseISO(date) : null}
                  format="dd.MM.yyyy HH:mm"
                  closeOnSelect={false}
                  slotProps={{
                    actionBar: { actions: ['cancel', 'accept'] },
                    textField: {
                      sx: { position: 'absolute', right: 0, bottom: 0, width: 0, height: 0, visibility: 'hidden' }
                    }
                  }}
                />
              </>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight="bold">Místo nehody</Typography>
          <Typography fontWeight="bold">{place}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight="bold">Stav</Typography>
          {status && <Chip size="small" label={status.label} variant="filled" sx={{ backgroundColor: status.color }} />}
        </Stack>
        {!!labels?.length && (
          <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="end">
            {labels.map((label) => (
              <TransparentChip key={label.type} size="small" color="info" label={label.label} />
            ))}
          </Stack>
        )}
        <Box my={1}>
          <Divider />
        </Box>
        {userRole === Role.Dealer && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography fontWeight="bold">Výše škody</Typography>
            <Typography fontWeight="bold">----</Typography>
          </Stack>
        )}

        {extra}
      </Stack>
    </Box>
  );
};
